import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { uploadFiles, getRole } from '../api/api';

const RoleUploadPage = () => {
  const navigate = useNavigate();
  const { roleId } = useParams();
  const [files, setFiles] = useState([]);
  const [role, setRole] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Fetch role data
  useEffect(() => {
    const fetchRole = async () => {
      try {
        const data = await getRole(roleId);
        setRole(data);
      } catch (err) {
        setError(err.detail || 'Error fetching role details.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchRole();
  }, [roleId]);

  // Handle file drop
  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
    onDrop: (acceptedFiles) => {
      setFiles([...files, ...acceptedFiles]);
    },
  });

  // Remove file from list
  const removeFile = (index) => {
    const newFiles = [...files];
    newFiles.splice(index, 1);
    setFiles(newFiles);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!files.length) {
      setError('Please upload at least one file');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('job_title', role.title);
      formData.append('job_requirements', role.requirements || '');
      
      // Add scoring criteria from the role
      for (const [criterion, details] of Object.entries(role.scoring_criteria)) {
        formData.append(`${criterion}_weight`, details.weight);
        if (details.threshold) {
          formData.append(`${criterion}_threshold`, details.threshold);
        }
      }

      files.forEach((file) => {
        formData.append('files', file);
      });

      const response = await uploadFiles(formData);
      navigate(`/evaluation/${response.batch_id}`);
    } catch (err) {
      setError(err.detail || 'Error uploading files. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto py-10 flex justify-center">
        <svg
          className="animate-spin h-8 w-8 text-primary-600"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
        <span className="ml-3 text-gray-600">Loading role details...</span>
      </div>
    );
  }

  if (!role) {
    return (
      <div className="max-w-4xl mx-auto py-10">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          Role not found. Please select a valid role.
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white shadow-md rounded-lg p-6 mb-8">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Upload CVs for Evaluation</h1>
        <div className="text-gray-600 mb-6">
          Using job role: <span className="font-medium">{role.title}</span>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Job details summary */}
          <div className="mb-6 bg-gray-50 p-4 rounded-md">
            <h2 className="text-lg font-semibold text-gray-700 mb-3">Job Details</h2>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Job Title
                </label>
                <div className="text-gray-800">{role.title}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Job Requirements
                </label>
                <div className="text-gray-800">
                  {role.requirements || 'No requirements specified'}
                </div>
              </div>
            </div>
          </div>

          {/* Scoring criteria summary */}
          <div className="mb-6 bg-gray-50 p-4 rounded-md">
            <h2 className="text-lg font-semibold text-gray-700 mb-3">Scoring Criteria</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(role.scoring_criteria).map(([key, value]) => (
                <div key={key} className="bg-white p-3 rounded shadow-sm">
                  <div className="flex justify-between items-center mb-1">
                    <span className="font-medium text-gray-800 capitalize">
                      {key.replace(/_/g, ' ')}
                    </span>
                    <span className="text-sm text-gray-600">
                      {Math.round(value.weight * 100)}%
                    </span>
                  </div>
                  {value.threshold && (
                    <div className="text-xs text-gray-500">
                      Threshold: {value.threshold} {key.includes('experience') ? 'years' : 'threshold'}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* File upload */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-700 mb-3">Upload CVs</h2>
            <div
              {...getRootProps()}
              className="border-2 border-dashed border-gray-300 rounded-md p-6 text-center cursor-pointer hover:bg-gray-50"
            >
              <input {...getInputProps()} />
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
              <p className="mt-2 text-sm text-gray-600">
                Drag and drop CV files here, or click to select files
              </p>
              <p className="mt-1 text-xs text-gray-500">
                Supported formats: PDF, DOC, DOCX
              </p>
            </div>
          </div>

          {/* File list */}
          {files.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Selected Files ({files.length})</h3>
              <ul className="divide-y divide-gray-200 border border-gray-200 rounded-md overflow-hidden">
                {files.map((file, index) => (
                  <li key={index} className="px-4 py-3 flex justify-between items-center bg-white">
                    <div className="flex items-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-gray-400 mr-3"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <span className="text-sm text-gray-900">{file.name}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate('/roles')}
              className="py-2 px-4 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || files.length === 0}
              className={`py-2 px-4 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 flex items-center ${
                (isSubmitting || files.length === 0) ? 'opacity-70 cursor-not-allowed' : ''
              }`}
            >
              {isSubmitting && (
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              )}
              {isSubmitting ? 'Uploading...' : 'Upload and Evaluate'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RoleUploadPage;
